import store from "@/store/index";
import axios from "axios";
import { getCookie } from "./getCookie";
import { computeMD5InWorker } from "./md5WorkerWrapper";

/**
 * Fetches raw nodes data from the specified site and bucket.
 *
 * @param {string} siteId - The ID of the site.
 * @param {string} bucketId - The ID of the bucket.
 * @param {Object} queryParameters - The query parameters to be included in the request.
 * @returns {Promise<Object>} The response data from the API.
 * @throws {Error} If the request fails.
 */
export const getNodesRaw = async (siteId, bucketId, queryParameters) => {
	const url = `${import.meta.env.VITE_API_BASE}/assetLink/groupware/${siteId}/buckets/${bucketId}`;
	try {
		const response = await axios.get(url, {
			auth: store.state.authObject,
			params: queryParameters,
		});
		return response.data;
	} catch (e) {
		throw new Error("Failed to get preconstruction nodes.");
	}
};

/**
 * Fetches the bucket information for a given site.
 *
 * @param {string} siteId - The ID of the site for which to fetch the bucket.
 * @param {Object} queryParameters - The query parameters to include in the request.
 * @returns {Promise<Object>} The response from the API containing the bucket information.
 * @throws {Error} If the request fails.
 */
export const getBuckets = async (siteId, queryParameters) => {
	const url = `${import.meta.env.VITE_API_BASE}/assetLink/groupware/${siteId}/buckets`;
	try {
		const response = await axios.get(url, {
			auth: store.state.authObject,
			params: queryParameters,
		});
		return response.data;
	} catch (e) {
		throw new Error("Failed to get preconstruction bucket.");
	}
};

/**
 * Lists nodes in a specified bucket.
 *
 * @param {string} bucket_id - The ID of the bucket.
 * @param {Object|null} params - Optional query parameters for filtering nodes.
 * @returns {Promise<Object>} The response containing the list of nodes.
 * @throws {Error} If the request fails.
 */
export const listNodes = async (bucket_id, params = null) => {
	try {
		let url =
			import.meta.env.VITE_LANDLOG_API_URL +
			`/file_storage/buckets/${bucket_id}/nodes`;
		if (params) url += `?${createParams(params)}`;

		const customRes = await axios
			.get(url, {
				headers: {
					Authorization: `Bearer ${getCookie("access_token")}`,
				},
				withCredentials: false,
			})
			.then((response) => ({ status: response.status, data: response.data }));

		return customRes;
	} catch (e) {
		throw e;
	}
};

/**
 * Uploads a file to Landlog S3 storage.
 *
 * @param {string} bucketId - The ID of the bucket where the file will be uploaded.
 * @param {string} parentNodeId - The ID of the parent node in the bucket.
 * @param {string} fileName - The name of the file to be uploaded.
 * @param {Uint8Array} fileData - The binary data of the file to be uploaded.
 * @returns {Promise<Object>} The response from the upload operation.
 * @throws {Error} If the upload fails.
 */
export const uploadLandlogS3 = async (
	bucketId,
	parentNodeId,
	fileName,
	fileData, // Browser File object
) => {
	// 1) Kick off listNodes and checksum in parallel
	// Using web worker for MD5 calculation to avoid blocking main thread
	const listPromise = listNodes(bucketId, { parent_node_id: parentNodeId });
	const time = performance.now();
	const checksumPromise = computeMD5InWorker(fileData);

	// 2) Wait for the existing nodes list
	const fileList = await listPromise;
	const existingNames = fileList.data.nodes.map((n) => n.name);

	// 3) Pick a unique upload name
	const uploadName = getUniqueOrgName(fileName, existingNames);

	// 4) Wait for the checksum (computed in web worker)
	const checksum = await checksumPromise;
	const time2 = performance.now();
	console.log(`MD5 checksum time: ${time2 - time} ms`);

	// 5) Build the signed‐PUT URL
	const params = createParams({
		name: uploadName,
		parent_node_id: parentNodeId,
		checksum,
	});
	const url =
		`${import.meta.env.VITE_LANDLOG_API_URL}/file_storage/buckets/` +
		`${bucketId}/upload?${params}`;

	// 6) Perform the upload
	const customRes = await axios
		.put(url, fileData, {
			headers: {
				Authorization: `Bearer ${getCookie("access_token")}`,
				"Content-Type": "application/octet-stream",
			},
			withCredentials: false,
			maxContentLength: Number.POSITIVE_INFINITY,
			maxBodyLength: Number.POSITIVE_INFINITY,
			timeout: 24 * 60 * 60 * 1000, // 24h
		})
		.then((res) => ({ status: res.status, data: res.data }));
	return customRes;
};

/**
 * Creates a query string from an object of parameters.
 *
 * @param {Object} params - The parameters to be converted into a query string.
 * @returns {URLSearchParams} The query string object.
 */
export const createParams = (params) => {
	const queryParams = new URLSearchParams();
	Object.keys(params).forEach((key) => {
		queryParams.append(key, params[key]);
	});
	return queryParams;
};

/**
 * Generates a unique file name by appending a numeric suffix if a duplicate is found.
 *
 * @param {string} orgName - The original file name.
 * @param {Array<string>} nodes - The list of existing file names to check for duplicates.
 * @returns {string} A unique file name.
 */
export const getUniqueOrgName = (orgName, nodes) => {
	const orgNameWithoutExt = orgName.substring(0, orgName.lastIndexOf("."));
	const extension = orgName.substring(orgName.lastIndexOf("."));
	let newOrgName = orgName;
	let count = 1;

	// Check if orgName itself is a duplicate
	while (nodes.some((node) => node === newOrgName)) {
		newOrgName = `${orgNameWithoutExt}_${String(count).padStart(3, "0")}${extension}`;
		count++;
	}

	return newOrgName;
};
